/**
 * The ligand interaction diagram of the MD analysis (the "Ligand-Protein
 * Contacts" page of a Schrödinger report): a 2D drawing of the ligand with the
 * protein residues that touch it around it, and a line or arrow for every
 * interaction that lasts more than a chosen fraction of the run, labelled with
 * that fraction.
 *
 * Everything here is pure (unit tested without a browser). `diagramModel`
 * chooses which interactions to draw and places the residue bubbles;
 * `interactionDiagramSvg` turns the model into one standalone SVG string, used
 * as it is on screen and rasterized to a picture for the PDF / PowerPoint
 * report (ui/charting/renderSvgToPng.js).
 *
 * Data (the md/1 packet): the compound's `ligand_2d` (RDKit's drawing of the
 * submitted ligand and where each heavy atom and ring is in it) and the rows of
 * `ligand_interactions` of each copy (and their mean, in `aggregate`):
 *   { type, ligand_atoms: [1-based], ring, residue: 'A:93', resname, group,
 *     ligand_role, occupancy }
 * type is hbond, hydrophobic, ionic, pi_pi, pi_cation or water_bridge.
 *
 * What is drawn, per type:
 *   hbond        magenta arrow from donor to acceptor, solid for a backbone atom
 *                of the residue and dashed for a side chain
 *   pi_pi        green line with round ends, from the centre of the ligand's ring
 *   pi_cation    red line with round ends
 *   ionic        a line shading from red (the negative end) to blue
 *   hydrophobic  no line, as in a Schrödinger diagram: the residue's green
 *                bubble is placed by the ligand part it touches (lines for
 *                every hydrophobic contact cross the whole drawing)
 *   water_bridge grey dashed line through a small "H2O" bubble to the residue
 */

export const INTERACTION_TYPES = {
    hbond: 'H-bond',
    hydrophobic: 'Hydrophobic',
    ionic: 'Salt bridge',
    pi_pi: 'π–π stacking',
    pi_cation: 'π–cation',
    water_bridge: 'Water bridge',
};

/** Fractions of the run the diagram can hide interactions below. */
export const DIAGRAM_THRESHOLDS = [0.1, 0.2, 0.3, 0.5];
export const DEFAULT_DIAGRAM_THRESHOLD = 0.3;

/** The picture's size in layout pixels (square: it sits beside its caption in a slide). */
export const DIAGRAM_SIZE = { width: 900, height: 900 };

// Bubble colours (the Maestro scheme): fill, edge.
const RESIDUE_CLASSES = {
    positive: { label: 'Charged (positive)', fill: '#b9a6f2', edge: '#7c5fd6' },
    negative: { label: 'Charged (negative)', fill: '#f29a9a', edge: '#d04848' },
    polar: { label: 'Polar', fill: '#94d6f2', edge: '#3aa3cf' },
    hydrophobic: { label: 'Hydrophobic', fill: '#b4de78', edge: '#74a62f' },
    glycine: { label: 'Glycine', fill: '#e4e4e4', edge: '#9a9a9a' },
    other: { label: 'Other', fill: '#f0e0b0', edge: '#bfa24a' },
    water: { label: 'Water', fill: '#e8e8e8', edge: '#a6a6a6' },
};

const CLASS_OF = {};
[
    ['positive', ['ARG', 'LYS', 'HIP']],
    ['negative', ['ASP', 'GLU']],
    ['polar', ['ASN', 'GLN', 'HID', 'HIE', 'HIS', 'SER', 'THR']],
    ['hydrophobic', ['ALA', 'CYS', 'ILE', 'LEU', 'MET', 'PHE', 'PRO', 'TRP', 'TYR', 'VAL']],
    ['glycine', ['GLY']],
].forEach(([name, residues]) => residues.forEach((r) => { CLASS_OF[r] = name; }));

const GLYPH_COLORS = {
    hbond: '#c026d3',
    pi_pi: '#2f9e44',
    pi_cation: '#e03131',
    hydrophobic: '#74a62f',
    water_bridge: '#8a8a8a',
    ionicNegative: '#e03131',
    ionicPositive: '#2563eb',
};

const BUBBLE_RADIUS = 27;
const WATER_RADIUS = 15;
const ATOM_CLEARANCE = 24;
const PANEL_MARGIN = 16;
// The finished picture is the content plus this margin, at least this wide.
const CROP_PAD = 22;
const MIN_WIDTH = 520;
// The bubble ring: the ligand's box scaled by RING_STRETCH (to clear its corners) plus a margin.
const RING_STRETCH = 1.15;
const RING_MARGIN = 72;
// The longest a bond is drawn (px): RDKit draws a small ligand to fill its
// canvas, so without this a benzamidine would be blown up to fill the panel.
const MAX_BOND_PX = 46;
const MIN_RING_RX = 230;
const MIN_RING_RY = 190;

/**
 * @param {string} resname
 * @returns {'positive'|'negative'|'polar'|'hydrophobic'|'glycine'|'other'}
 */
export function residueClass(resname) {
    return CLASS_OF[String(resname || '').toUpperCase()] || 'other';
}

/**
 * @param {string} key - a BMaps residue key, 'CHAIN:NUMBER[ICODE]'
 * @returns {{ chain: string, number: string }}
 */
export function parseResidueKey(key) {
    const text = String(key || '');
    const at = text.indexOf(':');
    return at < 0 ? { chain: '', number: text } : { chain: text.slice(0, at), number: text.slice(at + 1) };
}

function rowsOf(value) {
    if (Array.isArray(value)) return value;
    return Array.isArray(value?.rows) ? value.rows : [];
}

/**
 * The interaction rows to draw: one run's, or their mean.
 * @param {object} compound - a `compounds` entry of the md/1 packet
 * @param {'mean'|number} [run]
 * @returns {object[]}
 */
export function interactionRows(compound, run = 'mean') {
    const copies = compound?.copies || [];
    if (run !== 'mean') {
        return rowsOf(copies.find((c) => c.copy === Number(run))?.ligand_interactions);
    }
    const agg = rowsOf(compound?.aggregate?.ligand_interactions);
    if (agg.length) return agg;
    const only = copies.find((c) => rowsOf(c.ligand_interactions).length);
    return rowsOf(only?.ligand_interactions);
}

/** The runs of a compound that have interaction rows. */
export function interactionRuns(compound) {
    return (compound?.copies || []).filter((c) => rowsOf(c.ligand_interactions).length > 0)
        .map((c) => c.copy);
}

/** Whether a compound has what the diagram needs. */
export function hasInteractionDiagram(compound) {
    return !!(compound?.ligand_2d?.svg && Object.keys(compound.ligand_2d.atoms || {}).length
        && interactionRows(compound, 'mean').length);
}

/**
 * The text under the diagram.
 * @param {number} [threshold] - the fraction of the run
 * @returns {string}
 */
export function interactionDiagramCaption(threshold = DEFAULT_DIAGRAM_THRESHOLD) {
    return 'A schematic of the detailed interactions of the ligand atoms with the protein '
        + `residues. Interactions that occur more than ${Math.round(threshold * 100)}% of the `
        + 'simulation time (the mean over the independent runs) are shown, as the fraction of the '
        + 'analysed frames. It is possible to have interactions above 100%: a residue may make '
        + 'several interactions of one type with the same ligand atom (an Arg side chain has four '
        + 'H-bond donors that can all bond to one acceptor). Arrows run from the H-bond donor to '
        + 'the acceptor; a dashed arrow is a side-chain atom, a solid one a backbone atom. A '
        + 'water bridge is an H-bond from the ligand to a water that bonds the residue.';
}

export function esc(value) {
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

const num = (v) => Math.round(v * 10) / 10;

// ---------------------------------------------------------------------------
// Model: which interactions, and where everything goes
// ---------------------------------------------------------------------------

function legendFor(types, classes) {
    const items = [];
    for (const c of ['positive', 'negative', 'polar', 'hydrophobic', 'glycine', 'other']) {
        if (classes.has(c)) items.push({ kind: 'bubble', cls: c, label: RESIDUE_CLASSES[c].label });
    }
    if (classes.has('water')) items.push({ kind: 'bubble', cls: 'water', label: 'Water' });
    const glyphs = [
        ['hbond-backbone', 'hbond', 'H-bond (backbone)'],
        ['hbond-sidechain', 'hbond', 'H-bond (side chain)'],
        ['pi_pi', 'pi_pi', 'π–π stacking'],
        ['pi_cation', 'pi_cation', 'π–cation'],
        ['ionic', 'ionic', 'Salt bridge'],
        ['water_bridge', 'water_bridge', 'Water bridge'],
    ];
    for (const [glyph, , text] of glyphs) {
        if (types.has(glyph)) {
            items.push({ kind: 'glyph', glyph, label: text });
        }
    }
    return items;
}

// A legend entry's width in px (text at 12 px is about 6.4 px a character).
const legendWidth = (item) => (item.kind === 'bubble' ? 30 : 46) + item.label.length * 6.4 + 22;

function legendLayout(items, width) {
    const rows = [];
    let x = PANEL_MARGIN + 6;
    let y = 0;
    for (const item of items) {
        const w = legendWidth(item);
        if (x + w > width - PANEL_MARGIN && x > PANEL_MARGIN + 6) {
            x = PANEL_MARGIN + 6;
            y += 26;
        }
        rows.push({ ...item, x, y });
        x += w;
    }
    return { items: rows, height: items.length ? y + 34 : 0 };
}

/**
 * Positions along a ring (an ellipse) that keep their order and are `gap` apart
 * while staying as near as possible (least squares) to the wanted ones: the
 * nodes are unrolled at their widest gap and an isotonic regression moves them.
 * @param {object} ring - { rx, ry }
 * @param {number[]} wanted - angles, sorted ascending in (-pi, pi]
 * @param {number} gap - the least distance along the ring between neighbours
 * @returns {number[]} angles
 */
function ringAngles(ring, wanted, gap) {
    const samples = 720;
    const cum = [0];
    let prev = { x: ring.rx * Math.cos(-Math.PI), y: ring.ry * Math.sin(-Math.PI) };
    for (let i = 1; i <= samples; i += 1) {
        const t = -Math.PI + (2 * Math.PI * i) / samples;
        const here = { x: ring.rx * Math.cos(t), y: ring.ry * Math.sin(t) };
        cum.push(cum[i - 1] + Math.hypot(here.x - prev.x, here.y - prev.y));
        prev = here;
    }
    const length = cum[samples];
    const arcOf = (t) => {
        const f = ((t + Math.PI) / (2 * Math.PI)) * samples;
        const i = Math.min(samples - 1, Math.floor(f));
        return cum[i] + (cum[i + 1] - cum[i]) * (f - i);
    };
    const angleOf = (arc) => {
        const target = ((arc % length) + length) % length;
        let lo = 0;
        let hi = samples;
        while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (cum[mid] <= target) lo = mid; else hi = mid;
        }
        const f = lo + (target - cum[lo]) / ((cum[lo + 1] - cum[lo]) || 1);
        return -Math.PI + (2 * Math.PI * f) / samples;
    };
    const n = wanted.length;
    const arcs = wanted.map(arcOf);
    if (n === 1) return [angleOf(arcs[0])];
    // Unroll the circle at the widest gap between neighbours.
    let widest = 0;
    let start = 0;
    for (let i = 0; i < n; i += 1) {
        const next = i + 1 < n ? arcs[i + 1] : arcs[0] + length;
        if (next - arcs[i] > widest) {
            widest = next - arcs[i];
            start = (i + 1) % n;
        }
    }
    const order = [];
    for (let k = 0; k < n; k += 1) {
        const i = (start + k) % n;
        order.push({ i, arc: arcs[i] + (i < start ? length : 0) });
    }
    // Isotonic regression of arc - k * gap (pool adjacent violators).
    const blocks = [];
    order.forEach((o, k) => {
        blocks.push({ sum: o.arc - k * gap, count: 1 });
        while (blocks.length > 1) {
            const b = blocks[blocks.length - 1];
            const a = blocks[blocks.length - 2];
            if (a.sum / a.count <= b.sum / b.count) break;
            blocks.splice(blocks.length - 2, 2, { sum: a.sum + b.sum, count: a.count + b.count });
        }
    });
    const result = new Array(n);
    let k = 0;
    for (const block of blocks) {
        for (let c = 0; c < block.count; c += 1) {
            result[order[k].i] = angleOf(block.sum / block.count + k * gap);
            k += 1;
        }
    }
    return result;
}

/**
 * Gives each node a position on the ellipse: its own angle, moved apart from
 * its neighbours (in angle order, round the ring) until every pair is `gap`
 * apart. More nodes than fit go alternately on a second, outer ring.
 */
function spreadOnEllipse(nodes, ellipse, gap) {
    const mean = (ellipse.rx + ellipse.ry) / 2;
    const sorted = [...nodes].sort((a, b) => a.angle - b.angle || a.key.localeCompare(b.key));
    const outer = sorted.length * gap > 2 * Math.PI * mean * 0.85;
    const rings = outer ? [[], []] : [[]];
    sorted.forEach((n, i) => rings[outer ? i % 2 : 0].push(n));
    rings.forEach((ring, k) => {
        const rx = ellipse.rx + k * (BUBBLE_RADIUS + 6);
        const ry = ellipse.ry + k * (BUBBLE_RADIUS + 6);
        const angles = ringAngles({ rx, ry }, ring.map((n) => n.angle), gap * 1.04);
        ring.forEach((n, i) => {
            n.angle = angles[i];
            n.x = ellipse.x + rx * Math.cos(angles[i]);
            n.y = ellipse.y + ry * Math.sin(angles[i]);
        });
    });
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

function pointSegmentDistance(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function segmentsCross(a, b, c, d) {
    const o = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
    const d1 = o(c, d, a);
    const d2 = o(c, d, b);
    const d3 = o(a, b, c);
    const d4 = o(a, b, d);
    return d1 * d2 < 0 && d3 * d4 < 0;
}

/** Distance between two segments (0 when they cross). */
function segmentDistance(a, b, c, d) {
    if (segmentsCross(a, b, c, d)) return 0;
    return Math.min(
        pointSegmentDistance(a, c, d), pointSegmentDistance(b, c, d),
        pointSegmentDistance(c, a, b), pointSegmentDistance(d, a, b),
    );
}

/** The convex hull of points (counter-clockwise, monotone chain). */
function convexHull(points) {
    const pts = [...points].sort((p, q) => p.x - q.x || p.y - q.y);
    if (pts.length < 3) return pts.map((p) => ({ x: p.x, y: p.y }));
    const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const build = (list) => {
        const out = [];
        for (const p of list) {
            while (out.length >= 2
                && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop();
            out.push(p);
        }
        out.pop();
        return out;
    };
    return [...build(pts), ...build([...pts].reverse())].map((p) => ({ x: p.x, y: p.y }));
}

/**
 * Signed distance from a point to a hull (negative inside), as drawn: used to
 * keep bubbles off the ligand.
 * @param {{x: number, y: number}[]} hull
 * @param {{x: number, y: number}} p
 * @returns {number}
 */
export function distanceToHull(hull, p) {
    if (!hull.length) return Infinity;
    let edge = Infinity;
    for (let i = 0; i < hull.length; i += 1) {
        edge = Math.min(edge, pointSegmentDistance(p, hull[i], hull[(i + 1) % hull.length]));
    }
    if (hull.length < 3) return edge;
    let inside = true;
    for (let i = 0; i < hull.length; i += 1) {
        const a = hull[i];
        const b = hull[(i + 1) % hull.length];
        if ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x) < 0) inside = false;
    }
    return inside ? -edge : edge;
}

/** Where a ray from inside an ellipse leaves it: the distance along `dir`. */
function rayEllipse(origin, dir, ellipse) {
    const ox = (origin.x - ellipse.x) / ellipse.rx;
    const oy = (origin.y - ellipse.y) / ellipse.ry;
    const dx = dir.x / ellipse.rx;
    const dy = dir.y / ellipse.ry;
    const a = dx * dx + dy * dy;
    const b = 2 * (ox * dx + oy * dy);
    const c = ox * ox + oy * oy - 1;
    const disc = b * b - 4 * a * c;
    if (disc < 0 || c > 0) return 60;
    return (-b + Math.sqrt(disc)) / (2 * a);
}

/**
 * The ligand's bonds, inferred from the drawing (the packet has the atoms
 * only): atoms closer than 1.3 times the typical nearest-neighbour distance.
 * RDKit draws every bond about the same length.
 */
function inferBonds(atoms) {
    if (atoms.length < 2) return [];
    const nearest = atoms.map((a, i) => Math.min(...atoms.map(
        (b, j) => (i === j ? Infinity : dist(a, b)),
    )));
    const sorted = [...nearest].sort((x, y) => x - y);
    const limit = sorted[Math.floor(sorted.length / 2)] * 1.3;
    const bonds = [];
    for (let i = 0; i < atoms.length; i += 1) {
        for (let j = i + 1; j < atoms.length; j += 1) {
            if (dist(atoms[i], atoms[j]) <= limit) bonds.push([i, j]);
        }
    }
    return bonds;
}

/**
 * The direction a line leaves the ligand from `origin`: the clear one nearest
 * the way the atom already points (away from its neighbours), so a line to a
 * bubble does not run over other atoms or bonds. A direction is clear when it
 * stays 14 px off every other atom and does not touch a bond.
 * @returns {{ x: number, y: number }} a unit vector
 */
function outwardDirection(origin, preferred, ellipse, geo, own) {
    const base = Math.atan2(preferred.y, preferred.x);
    let best = null;
    for (let k = 0; k <= 36; k += 1) {
        for (const sign of k === 0 ? [1] : [1, -1]) {
            const angle = base + sign * k * (Math.PI / 36);
            const dir = { x: Math.cos(angle), y: Math.sin(angle) };
            const reach = rayEllipse(origin, dir, ellipse);
            const end = { x: origin.x + dir.x * reach, y: origin.y + dir.y * reach };
            let clearance = Infinity;
            geo.atoms.forEach((atom, i) => {
                if (own.has(i)) return;
                clearance = Math.min(clearance, pointSegmentDistance(atom, origin, end) - 14);
            });
            geo.bonds.forEach(([i, j]) => {
                if (own.has(i) || own.has(j)) return;
                const gap = segmentDistance(origin, end, geo.atoms[i], geo.atoms[j]);
                clearance = Math.min(clearance, gap - 8);
            });
            if (clearance >= 0) return { dir, end };
            if (!best || clearance > best.clearance) best = { dir, end, clearance };
        }
    }
    return best;
}

/**
 * Pushes bubbles off each other, the fixed obstacles and the ligand's hull, and
 * keeps them inside the panel. Deterministic: a fixed number of passes in a
 * fixed order.
 */
function relax(nodes, obstacles, bounds, hull) {
    const centre = hull.length
        ? {
            x: hull.reduce((s, p) => s + p.x, 0) / hull.length,
            y: hull.reduce((s, p) => s + p.y, 0) / hull.length,
        }
        : null;
    const pass = () => {
        for (let i = 0; i < nodes.length; i += 1) {
            for (let j = i + 1; j < nodes.length; j += 1) {
                const a = nodes[i];
                const b = nodes[j];
                let dx = b.x - a.x;
                let dy = b.y - a.y;
                let d = Math.hypot(dx, dy);
                const min = a.r + b.r + 12;
                if (d < min) {
                    if (d < 1e-6) {
                        const angle = (i * 2.399963 + j) % (2 * Math.PI);
                        dx = Math.cos(angle);
                        dy = Math.sin(angle);
                        d = 1;
                    }
                    const push = (min - d) / 2 / d;
                    a.x -= dx * push;
                    a.y -= dy * push;
                    b.x += dx * push;
                    b.y += dy * push;
                }
            }
        }
        for (const n of nodes) {
            for (const o of obstacles) {
                const dx = n.x - o.x;
                const dy = n.y - o.y;
                const d = Math.hypot(dx, dy);
                const min = n.r + o.r;
                if (d < min) {
                    const ux = d < 1e-6 ? 1 : dx / d;
                    const uy = d < 1e-6 ? 0 : dy / d;
                    n.x = o.x + ux * min;
                    n.y = o.y + uy * min;
                }
            }
            if (centre) {
                const gap = distanceToHull(hull, n) - (n.r + 6);
                if (gap < 0) {
                    const d = Math.hypot(n.x - centre.x, n.y - centre.y) || 1;
                    n.x += ((n.x - centre.x) / d) * -gap;
                    n.y += ((n.y - centre.y) / d) * -gap;
                }
            }
            n.x = Math.min(Math.max(n.x, bounds.left + n.r), bounds.right - n.r);
            n.y = Math.min(Math.max(n.y, bounds.top + n.r), bounds.bottom - n.r);
        }
    };
    for (let i = 0; i < 300; i += 1) pass();
}

/**
 * Chooses the interactions to draw and places the residue bubbles around the
 * ligand drawing. The bubbles are on a ring outside the ligand, each at the
 * angle where a clear line leaves its ligand atom; a water bridge's H2O bubble
 * sits between the ligand's edge and the residue. The picture is then cropped
 * to its content (so a ligand with few interactions gives a compact picture).
 * @param {object} compound - a `compounds` entry of the md/1 packet
 * @param {object} [opts]
 * @param {'mean'|number} [opts.run]
 * @param {number} [opts.threshold] - fraction of the run an interaction must last
 * @param {number} [opts.width] - the working width, cropped afterwards
 * @param {number} [opts.height] - the working height, cropped afterwards
 * @param {string} [opts.idPrefix] - prefix of the SVG element ids, unique per diagram on a page
 * @returns {object|null} null when the compound has no ligand drawing or no rows
 */
export function diagramModel(compound, {
    run = 'mean', threshold = DEFAULT_DIAGRAM_THRESHOLD, ...size
} = {}) {
    const ligand = compound?.ligand_2d;
    if (!ligand?.svg || !ligand.atoms) return null;
    const all = interactionRows(compound, run);
    if (all.length === 0) return null;
    const width = size.width || DIAGRAM_SIZE.width;
    const maxHeight = size.height || DIAGRAM_SIZE.height;
    const idPrefix = size.idPrefix || 'lid';
    const rows = all.filter((r) => Number(r.occupancy) >= threshold - 1e-9)
        .map((r, index) => ({ ...r, order: index }))
        .sort((a, b) => String(a.residue).localeCompare(String(b.residue), undefined,
            { numeric: true }) || a.type.localeCompare(b.type) || a.order - b.order);

    const lw = Number(ligand.width) || 600;
    const lh = Number(ligand.height) || 450;
    const atomIds = Object.keys(ligand.atoms);
    const indexOfAtom = new Map(atomIds.map((id, i) => [id, i]));

    // Where each row touches the ligand (atom indices, or a ring).
    const touch = (row) => {
        const ring = row.ring !== null && row.ring !== undefined ? ligand.rings?.[row.ring] : null;
        if (ring) {
            return {
                ring: true,
                x: ring.x,
                y: ring.y,
                own: new Set((ring.atoms || []).map((a) => indexOfAtom.get(String(a)))),
            };
        }
        const idx = (row.ligand_atoms || []).map((a) => indexOfAtom.get(String(a)))
            .filter((i) => i !== undefined);
        if (!idx.length) return null;
        const pts = idx.map((i) => ligand.atoms[atomIds[i]]);
        return {
            ring: false,
            x: pts.reduce((sum, p) => sum + p.x, 0) / pts.length,
            y: pts.reduce((sum, p) => sum + p.y, 0) / pts.length,
            element: pts[0].element,
            charge: pts[0].charge || 0,
            own: new Set(idx),
        };
    };
    const edges = [];
    for (const row of rows) {
        const raw = touch(row);
        if (raw) edges.push({ row, raw });
    }

    const types = new Set();
    const classes = new Set();
    const legendTypes = new Set();
    for (const e of edges) {
        const { row } = e;
        if (row.type === 'hbond') {
            legendTypes.add(row.group === 'backbone' ? 'hbond-backbone' : 'hbond-sidechain');
        } else {
            legendTypes.add(row.type);
        }
        types.add(row.type);
        classes.add(residueClass(row.resname));
    }
    if (types.has('water_bridge')) classes.add('water');
    const legendItems = legendFor(legendTypes, classes);
    const legend0 = legendLayout(legendItems, width);

    // The ligand drawing sits in the middle of the working panel, as large as
    // the bubble ring around it allows.
    const top = PANEL_MARGIN;
    const xs = atomIds.map((id) => ligand.atoms[id].x);
    const ys = atomIds.map((id) => ligand.atoms[id].y);
    const box = {
        x: (Math.min(...xs) + Math.max(...xs)) / 2,
        y: (Math.min(...ys) + Math.max(...ys)) / 2,
        hw: Math.max((Math.max(...xs) - Math.min(...xs)) / 2, 40),
        hh: Math.max((Math.max(...ys) - Math.min(...ys)) / 2, 40),
    };
    const ringGap = 2 * BUBBLE_RADIUS + 8;
    const roomX = width / 2 - PANEL_MARGIN - BUBBLE_RADIUS - RING_MARGIN;
    const roomY = (maxHeight - legend0.height - 2 * PANEL_MARGIN) / 2 - BUBBLE_RADIUS - RING_MARGIN;
    const rawAtoms = atomIds.map((id) => ligand.atoms[id]);
    const nearestRaw = rawAtoms.map((a, i) => Math.min(...rawAtoms.map(
        (b, j) => (i === j ? Infinity : dist(a, b)),
    ))).filter(Number.isFinite).sort((x, y) => x - y);
    const bondRaw = nearestRaw.length ? nearestRaw[Math.floor(nearestRaw.length / 2)] : 0;
    const scale = Math.min(1.5, roomX / (box.hw * RING_STRETCH), roomY / (box.hh * RING_STRETCH),
        bondRaw > 0 ? MAX_BOND_PX / bondRaw : Infinity);
    const bottom = maxHeight - PANEL_MARGIN - legend0.height;
    const cx = width / 2;
    const cy = (top + bottom) / 2;
    const place = (p) => ({ x: cx + (p.x - box.x) * scale, y: cy + (p.y - box.y) * scale });
    const origin = { x: cx - box.x * scale, y: cy - box.y * scale };
    const atoms = atomIds.map((id) => ({
        id, ...place(ligand.atoms[id]), element: ligand.atoms[id].element,
    }));
    for (const e of edges) e.point = { ...e.raw, ...place(e.raw) };
    const bonds = inferBonds(atoms);
    const geo = { atoms, bonds: bonds.map(([i, j]) => [i, j]) };
    const hull = convexHull(atoms);
    const molCentre = {
        x: atoms.reduce((sum, a) => sum + a.x, 0) / atoms.length,
        y: atoms.reduce((sum, a) => sum + a.y, 0) / atoms.length,
    };
    const neighbours = atoms.map(() => []);
    bonds.forEach(([i, j]) => {
        neighbours[i].push(j);
        neighbours[j].push(i);
    });
    // Around a small ligand the ring keeps a minimum size, so the lines are long
    // enough to carry their labels.
    const ellipse = {
        x: cx,
        y: cy,
        rx: Math.max(MIN_RING_RX, box.hw * scale * RING_STRETCH + RING_MARGIN),
        ry: Math.max(MIN_RING_RY, box.hh * scale * RING_STRETCH + RING_MARGIN),
    };

    // The line of each interaction leaves the ligand in a clear direction near
    // the way its atom points (away from its neighbours).
    const exitOf = (e, index) => {
        let pref = { x: 0, y: 0 };
        if (e.raw.ring) {
            pref = { x: e.point.x - molCentre.x, y: e.point.y - molCentre.y };
        } else {
            for (const i of e.raw.own) {
                const around = neighbours[i];
                const centre = around.length
                    ? {
                        x: around.reduce((sum, j) => sum + atoms[j].x, 0) / around.length,
                        y: around.reduce((sum, j) => sum + atoms[j].y, 0) / around.length,
                    }
                    : molCentre;
                const v = { x: atoms[i].x - centre.x, y: atoms[i].y - centre.y };
                const len = Math.hypot(v.x, v.y) || 1;
                pref.x += v.x / len;
                pref.y += v.y / len;
            }
        }
        if (Math.hypot(pref.x, pref.y) < 1e-6) {
            const angle = (index * 2.399963) % (2 * Math.PI);
            pref = { x: Math.cos(angle), y: Math.sin(angle) };
        }
        return outwardDirection(e.point, pref, ellipse, geo, e.raw.own);
    };
    edges.forEach((e, index) => { e.exit = exitOf(e, index); });

    // One bubble per residue, as in Schrödinger's diagram: every interaction of
    // a residue goes to the same bubble.
    const clusters = [];
    const byResidue = new Map();
    for (const e of edges) {
        if (!byResidue.has(e.row.residue)) byResidue.set(e.row.residue, []);
        byResidue.get(e.row.residue).push(e);
    }
    for (const list of byResidue.values()) {
        clusters.push({ residue: list[0].row.residue, k: 0, edges: list });
    }
    const maxRx = width / 2 - PANEL_MARGIN - BUBBLE_RADIUS;
    const maxRy = (bottom - top) / 2 - BUBBLE_RADIUS;
    const perimeter = () => (2 * Math.PI * Math.hypot(ellipse.rx, ellipse.ry)) / Math.SQRT2;
    while (clusters.length * ringGap * 1.15 > perimeter()
        && (ellipse.rx < maxRx || ellipse.ry < maxRy)) {
        ellipse.rx = Math.min(maxRx, ellipse.rx + 12);
        ellipse.ry = Math.min(maxRy, ellipse.ry + 12);
    }

    const nodes = [];
    clusters.forEach((cluster) => {
        // The bubble goes where the strongest drawn interaction's line leaves the
        // ligand (a hydrophobic contact has no line; it only places the bubble).
        const drawnFirst = (e) => (e.row.type === 'hydrophobic' ? 0 : 1);
        const primary = cluster.edges.reduce((x, y) => (
            drawnFirst(y) > drawnFirst(x)
            || (drawnFirst(y) === drawnFirst(x) && y.row.occupancy > x.row.occupancy) ? y : x));
        // A residue bound at several points (a bidentate salt bridge) sits between
        // them: the mean of its drawn lines' exits, weighted by occupancy.
        const drawn = cluster.edges.filter((e) => e.row.type !== 'hydrophobic');
        const pull = drawn.length > 1 ? drawn : [primary];
        const occ = (e) => Number(e.row.occupancy || 0);
        const w = pull.reduce((sum, e) => sum + occ(e), 0) || 1;
        const out = {
            end: {
                x: pull.reduce((sum, e) => sum + e.exit.end.x * occ(e), 0) / w,
                y: pull.reduce((sum, e) => sum + e.exit.end.y * occ(e), 0) / w,
            },
        };
        const angle = Math.atan2((out.end.y - cy) / ellipse.ry, (out.end.x - cx) / ellipse.rx);
        const first = cluster.edges[0].row;
        const { chain, number } = parseResidueKey(cluster.residue);
        const node = {
            id: `res-${cluster.residue}-${cluster.k}`,
            kind: 'residue',
            key: `${cluster.residue}#${cluster.k}`,
            residue: cluster.residue,
            chain,
            number,
            resname: first.resname,
            cls: residueClass(first.resname),
            r: BUBBLE_RADIUS,
            angle,
            x: 0,
            y: 0,
        };
        cluster.edges.forEach((e) => { e.node = node; });
        nodes.push(node);
    });
    spreadOnEllipse(nodes, ellipse, ringGap);
    const atomObstacles = atoms.map((a) => ({ x: a.x, y: a.y, r: ATOM_CLEARANCE }));
    const panel = {
        left: PANEL_MARGIN, right: width - PANEL_MARGIN, top, bottom,
    };
    relax(nodes, atomObstacles, panel, hull);

    // A water bridge's H2O bubble: just outside the ligand's edge on the line
    // to the residue, between the edge and the residue's bubble.
    const waters = [];
    for (const e of edges) {
        if (e.row.type !== 'water_bridge') continue;
        const res = e.node;
        const total = dist(e.point, res);
        const ux = (res.x - e.point.x) / (total || 1);
        const uy = (res.y - e.point.y) / (total || 1);
        let exit = 0;
        while (exit < total && distanceToHull(hull, {
            x: e.point.x + ux * exit, y: e.point.y + uy * exit,
        }) < WATER_RADIUS + 8) exit += 3;
        const last = total - (res.r + WATER_RADIUS + 6);
        // Somewhere between the ligand's edge and the bubble, and off the other lines.
        const room = Math.max(last - exit, 0);
        let t = exit + room / 2;
        let best = -Infinity;
        for (const f of [0.5, 0.35, 0.65, 0.2, 0.8, 0, 1]) {
            const at = exit + room * f;
            const spot = { x: e.point.x + ux * at, y: e.point.y + uy * at };
            const gap = Math.min(...edges.filter((o) => o !== e).map(
                (o) => pointSegmentDistance(spot, o.point, o.node) - (WATER_RADIUS + 5),
            ), 0);
            if (gap > best) {
                best = gap;
                t = at;
            }
            if (gap >= 0) break;
        }
        const water = {
            id: `water-${e.row.order}`,
            kind: 'water',
            r: WATER_RADIUS,
            x: e.point.x + ux * t,
            y: e.point.y + uy * t,
        };
        e.water = water;
        waters.push(water);
    }
    const fixed = [...nodes.map((n) => ({ x: n.x, y: n.y, r: n.r })), ...atomObstacles];
    relax(waters, fixed, panel, hull);
    nodes.push(...waters);

    // Several interactions with one bubble run side by side.
    const perNode = new Map();
    const lined = edges.filter((e) => e.row.type !== 'hydrophobic');
    for (const e of lined) perNode.set(e.node, (perNode.get(e.node) || 0) + 1);
    const seen = new Map();
    for (const e of lined) {
        const k = seen.get(e.node) || 0;
        seen.set(e.node, k + 1);
        e.offset = (k - (perNode.get(e.node) - 1) / 2) * 8;
    }

    // Crop to the content, and put the legend under it.
    const content = {
        minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity,
    };
    const grow = (x, y, r) => {
        content.minX = Math.min(content.minX, x - r);
        content.maxX = Math.max(content.maxX, x + r);
        content.minY = Math.min(content.minY, y - r);
        content.maxY = Math.max(content.maxY, y + r);
    };
    atoms.forEach((a) => grow(a.x, a.y, 18));
    nodes.forEach((n) => grow(n.x, n.y, n.r + 2));
    const contentW = content.maxX - content.minX;
    const finalWidth = Math.round(Math.max(contentW + 2 * CROP_PAD, MIN_WIDTH));
    const legend = legendLayout(legendItems, finalWidth);
    const finalHeight = Math.round(content.maxY - content.minY + 2 * CROP_PAD + legend.height);
    const dx = (finalWidth - contentW) / 2 - content.minX;
    const dy = CROP_PAD - content.minY;
    const move = (o) => {
        o.x = num(o.x + dx);
        o.y = num(o.y + dy);
    };
    nodes.forEach(move);
    edges.forEach((e) => move(e.point));
    atoms.forEach(move);
    hull.forEach(move);
    move(origin);

    return {
        width: finalWidth,
        height: finalHeight,
        threshold,
        idPrefix,
        ligand: {
            svg: ligand.svg, width: lw, height: lh, scale, x: origin.x, y: origin.y,
        },
        nodes,
        edges: edges.map((e) => ({
            row: e.row, point: e.point, node: e.node, water: e.water, offset: e.offset,
        })),
        geometry: { atoms, bonds: geo.bonds },
        hull,
        legend,
        empty: edges.length === 0,
    };
}

// ---------------------------------------------------------------------------
// SVG
// ---------------------------------------------------------------------------

const FONT = "font-family=\"'Helvetica Neue', Helvetica, Arial, sans-serif\"";

/** The inner markup of RDKit's SVG (its outer <svg> is replaced, scripts dropped). */
export function embeddedLigand(ligand) {
    const body = String(ligand.svg)
        .replace(/<\?xml[^>]*\?>/g, '')
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*')/gi, '')
        .replace(/<svg\b[^>]*>/i, '')
        .replace(/<\/svg>\s*$/i, '');
    return `<g transform="translate(${num(ligand.x)} ${num(ligand.y)}) scale(${num(ligand.scale * 1000) / 1000})">`
        + `<svg x="0" y="0" width="${ligand.width}" height="${ligand.height}" `
        + `viewBox="0 0 ${ligand.width} ${ligand.height}" overflow="visible">${body}</svg></g>`;
}

function unit(from, to) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const d = Math.hypot(dx, dy) || 1;
    return { x: dx / d, y: dy / d, d };
}

function bubbleSvg(node) {
    if (node.kind === 'water') {
        const c = RESIDUE_CLASSES.water;
        return `<g><circle cx="${node.x}" cy="${node.y}" r="${node.r}" fill="${c.fill}" `
            + `stroke="${c.edge}" stroke-width="1.5"/>`
            + `<text x="${node.x}" y="${node.y + 4}" text-anchor="middle" font-size="11" ${FONT} `
            + 'fill="#333">H<tspan font-size="8" dy="2">2</tspan><tspan dy="-2">O</tspan></text></g>';
    }
    const c = RESIDUE_CLASSES[node.cls];
    const lines = [];
    if (node.chain) lines.push([`${esc(node.chain)}:`, 9, 400]);
    lines.push([esc(node.resname || '?'), 11.5, 700]);
    lines.push([esc(node.number), 10.5, 400]);
    const step = 11.5;
    const y0 = node.y - ((lines.length - 1) * step) / 2 + 3.5;
    const text = lines.map(([t, size, weight], i) => `<text x="${node.x}" y="${num(y0 + i * step)}" `
        + `text-anchor="middle" font-size="${size}" font-weight="${weight}" ${FONT} fill="#1d1d1d">`
        + `${t}</text>`).join('');
    return `<g><circle cx="${node.x}" cy="${node.y}" r="${node.r}" fill="${c.fill}" `
        + `stroke="${c.edge}" stroke-width="1.5"/>${text}</g>`;
}

function label(x, y, text, color, size = 11.5) {
    return `<text x="${num(x)}" y="${num(y + 4)}" text-anchor="middle" font-size="${size}" ${FONT} `
        + `fill="${color}" stroke="#ffffff" stroke-width="3.2" paint-order="stroke" `
        + `stroke-linejoin="round">${esc(text)}</text>`;
}

const percent = (occupancy) => `${Math.round(occupancy * 100)}%`;

/** A ligand atom's start offset: a drawn element label needs room, a carbon does not. */
function startOffset(point) {
    if (point.ring) return 0;
    return point.element && point.element !== 'C' ? 11 : 4;
}

/**
 * Where a line's percentage goes: along the line, off the ligand's atoms and
 * bonds, the bubbles and the labels already placed (tried at several points
 * along the line, then to either side of it).
 */
function labelSpot(start, end, space) {
    const len = Math.hypot(end.x - start.x, end.y - start.y) || 1;
    const nx = -(end.y - start.y) / len;
    const ny = (end.x - start.x) / len;
    const clearance = (p) => {
        let least = Infinity;
        for (const a of space.atoms) least = Math.min(least, dist(p, a) - 15);
        for (const [a, b] of space.bonds) {
            least = Math.min(least, pointSegmentDistance(p, space.atoms[a], space.atoms[b]) - 11);
        }
        for (const n of space.nodes) least = Math.min(least, dist(p, n) - (n.r + 9));
        for (const l of space.placed) least = Math.min(least, dist(p, l) - 26);
        return least;
    };
    let best = null;
    for (const side of [0, 11, -11]) {
        for (const t of [0.6, 0.45, 0.75, 0.32, 0.88]) {
            const p = {
                x: start.x + (end.x - start.x) * t + nx * side,
                y: start.y + (end.y - start.y) * t + ny * side,
            };
            const c = clearance(p);
            if (c >= 0) return p;
            if (!best || c > best.c) best = { c, p };
        }
    }
    return best.p;
}

function edgeSvg(edge, ids, space) {
    const { row, point } = edge;
    // A hydrophobic contact is shown by its residue's bubble alone.
    if (row.type === 'hydrophobic') return '';
    const target = edge.water || edge.node;
    const t = unit(point, target);
    const off = startOffset(point);
    const side = edge.offset || 0;
    const start = { x: point.x + t.x * off - t.y * side, y: point.y + t.y * off + t.x * side };
    const end = {
        x: target.x - t.x * (target.r + 2) - t.y * side,
        y: target.y - t.y * (target.r + 2) + t.x * side,
    };
    const mid = labelSpot(start, end, space);
    space.placed.push(mid);
    const text = percent(row.occupancy);
    const line = (a, b, attrs) => `<line x1="${num(a.x)}" y1="${num(a.y)}" x2="${num(b.x)}" `
        + `y2="${num(b.y)}" ${attrs}/>`;
    const dot = (p, color) => `<circle cx="${num(p.x)}" cy="${num(p.y)}" r="3.2" fill="${color}"/>`;
    switch (row.type) {
        case 'hbond': {
            const color = GLYPH_COLORS.hbond;
            const dash = row.group === 'sidechain' ? ' stroke-dasharray="6 3.5"' : '';
            // Donor to acceptor: the ligand's role says which end is the head.
            const toLigand = row.ligand_role === 'acceptor';
            const [a, b] = toLigand ? [end, start] : [start, end];
            return `<g>${line(a, b, `stroke="${color}" stroke-width="1.6"${dash} marker-end="url(#${ids}-arrow)"`)}`
                + `${label(mid.x, mid.y, text, color)}</g>`;
        }
        case 'pi_pi':
            return `<g>${line(start, end, `stroke="${GLYPH_COLORS.pi_pi}" stroke-width="1.8"`)}`
                + `${dot(start, GLYPH_COLORS.pi_pi)}${dot(end, GLYPH_COLORS.pi_pi)}`
                + `${label(mid.x, mid.y, text, GLYPH_COLORS.pi_pi)}</g>`;
        case 'pi_cation':
            return `<g>${line(start, end, `stroke="${GLYPH_COLORS.pi_cation}" stroke-width="1.8"`)}`
                + `${dot(start, GLYPH_COLORS.pi_cation)}${dot(end, GLYPH_COLORS.pi_cation)}`
                + `${label(mid.x, mid.y, text, GLYPH_COLORS.pi_cation)}</g>`;
        case 'ionic': {
            const ligandPositive = (point.charge || 0) > 0;
            const [c1, c2] = ligandPositive
                ? [GLYPH_COLORS.ionicPositive, GLYPH_COLORS.ionicNegative]
                : [GLYPH_COLORS.ionicNegative, GLYPH_COLORS.ionicPositive];
            const id = `${ids}-ion-${row.order}`;
            return `<g><linearGradient id="${id}" gradientUnits="userSpaceOnUse" `
                + `x1="${num(start.x)}" y1="${num(start.y)}" x2="${num(end.x)}" y2="${num(end.y)}">`
                + `<stop offset="0" stop-color="${c1}"/><stop offset="1" stop-color="${c2}"/>`
                + `</linearGradient>${line(start, end, `stroke="url(#${id})" stroke-width="2.4"`)}`
                + `${label(mid.x, mid.y, text, '#7a2bbf')}</g>`;
        }
        case 'water_bridge': {
            const color = GLYPH_COLORS.water_bridge;
            const water = edge.water;
            const wr = unit(water, edge.node);
            const w1 = { x: water.x + wr.x * (water.r + 1), y: water.y + wr.y * (water.r + 1) };
            const reach = edge.node.r + 2;
            const w2 = { x: edge.node.x - wr.x * reach, y: edge.node.y - wr.y * reach };
            return `<g>${line(start, end, `stroke="${color}" stroke-width="1.5" stroke-dasharray="4 3"`)}`
                + `${line(w1, w2, `stroke="${color}" stroke-width="1.5" stroke-dasharray="4 3"`)}`
                + `${label(mid.x, mid.y, text, '#6b6b6b')}</g>`;
        }
        default: // hydrophobic
            return `<g>${line(start, end, `stroke="${GLYPH_COLORS.hydrophobic}" stroke-width="1.1" `
                + 'stroke-dasharray="1.5 3.5" stroke-linecap="round"')}`
                + `${label(mid.x, mid.y, text, '#5f8a22', 10)}</g>`;
    }
}

function legendSvg(model) {
    const { legend, height } = model;
    const y0 = height - legend.height + 6;
    const parts = legend.items.map((item) => {
        const y = y0 + item.y + 11;
        if (item.kind === 'bubble') {
            const c = RESIDUE_CLASSES[item.cls];
            return `<circle cx="${item.x + 9}" cy="${y}" r="8.5" fill="${c.fill}" stroke="${c.edge}" `
                + `stroke-width="1.3"/><text x="${item.x + 24}" y="${y + 4}" font-size="12" ${FONT} `
                + `fill="#222">${esc(item.label)}</text>`;
        }
        const x1 = item.x;
        const x2 = item.x + 30;
        let glyph;
        switch (item.glyph) {
            case 'hbond-backbone':
                glyph = `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${GLYPH_COLORS.hbond}" stroke-width="1.6" marker-end="url(#${model.idPrefix}-arrow)"/>`;
                break;
            case 'hbond-sidechain':
                glyph = `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${GLYPH_COLORS.hbond}" stroke-width="1.6" stroke-dasharray="6 3.5" marker-end="url(#${model.idPrefix}-arrow)"/>`;
                break;
            case 'pi_pi':
            case 'pi_cation': {
                const color = GLYPH_COLORS[item.glyph];
                glyph = `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${color}" stroke-width="1.8"/>`
                    + `<circle cx="${x1}" cy="${y}" r="3.2" fill="${color}"/><circle cx="${x2}" cy="${y}" r="3.2" fill="${color}"/>`;
                break;
            }
            case 'ionic':
                glyph = `<linearGradient id="${model.idPrefix}-legend-ion" gradientUnits="userSpaceOnUse" x1="${x1}" y1="0" x2="${x2}" y2="0"><stop offset="0" stop-color="${GLYPH_COLORS.ionicNegative}"/><stop offset="1" stop-color="${GLYPH_COLORS.ionicPositive}"/></linearGradient>`
                    + `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="url(#${model.idPrefix}-legend-ion)" stroke-width="2.4"/>`;
                break;
            case 'water_bridge':
                glyph = `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${GLYPH_COLORS.water_bridge}" stroke-width="1.5" stroke-dasharray="4 3"/>`;
                break;
            default:
                glyph = `<line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${GLYPH_COLORS.hydrophobic}" stroke-width="1.1" stroke-dasharray="1.5 3.5" stroke-linecap="round"/>`;
        }
        return `${glyph}<text x="${x2 + 8}" y="${y + 4}" font-size="12" ${FONT} fill="#222">${esc(item.label)}</text>`;
    });
    return `<g>${parts.join('')}</g>`;
}

/**
 * The diagram as one standalone SVG document string.
 * @param {object|null} model - from {@link diagramModel}
 * @returns {string} '' for a null model
 */
export function interactionDiagramSvg(model) {
    if (!model) return '';
    const { width, height, idPrefix } = model;
    const space = {
        atoms: model.geometry.atoms, bonds: model.geometry.bonds, nodes: model.nodes, placed: [],
    };
    const edgeMarkup = model.edges.map((edge) => edgeSvg(edge, idPrefix, space)).join('');
    const arrow = `<marker id="${idPrefix}-arrow" viewBox="0 0 10 10" refX="9" refY="5" `
        + 'markerWidth="7" markerHeight="7" orient="auto-start-reverse">'
        + `<path d="M0,0 L10,5 L0,10 z" fill="${GLYPH_COLORS.hbond}"/></marker>`;
    const note = model.empty || model.nodes.length === 0
        ? `<text x="${width / 2}" y="${PANEL_MARGIN + 18}" text-anchor="middle" font-size="13" ${FONT} fill="#555">`
            + `No interaction lasts more than ${Math.round(model.threshold * 100)}% of the run</text>`
        : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" `
        + `viewBox="0 0 ${width} ${height}" role="img" aria-label="Ligand interaction diagram">`
        + `<defs>${arrow}</defs>`
        + `<rect x="0" y="0" width="${width}" height="${height}" rx="10" fill="#ffffff" `
        + 'stroke="#d0d0d0" stroke-width="1"/>'
        + `${embeddedLigand(model.ligand)}${edgeMarkup}${model.nodes.map(bubbleSvg).join('')}`
        + `${note}${legendSvg(model)}</svg>`;
}

/**
 * The report picture's size (layout pixels) for a compound.
 * @param {object} compound
 * @returns {{ width: number, height: number }}
 */
export function interactionDiagramSize(compound) {
    const model = diagramModel(compound, { run: 'mean' });
    return model ? { width: model.width, height: model.height } : DIAGRAM_SIZE;
}

/**
 * The report's picture of the diagram, as an SVG string: the mean over the
 * runs, interactions above the default threshold.
 * @param {object} compound
 * @returns {string}
 */
export function interactionDiagramReportSvg(compound) {
    return interactionDiagramSvg(diagramModel(compound, { run: 'mean', idPrefix: 'rep' }));
}
